import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getStudentProfile } from '../api/authApi';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        
        console.log('Initializing auth with token:', !!token);
        
        if (token) {
          // Decode token to get initial user info
          const decodedToken = jwtDecode(token);
          console.log('Decoded token:', decodedToken);
          
          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp < currentTime) {
            console.log('Token expired, logging out');
            handleLogout();
            return;
          }
          
          // Determine if user is admin
          const isAdminUser = decodedToken.is_staff || false;
          console.log('Is admin user:', isAdminUser);
          
          setIsAdmin(isAdminUser);
          
          if (isAdminUser) {
            // For admin, set basic info from token
            setUser({
              email: decodedToken.email || '',
              is_staff: true
            });
            console.log('Set admin user:', decodedToken.email);
          } else {
            // For student, fetch full profile
            try {
              const profileData = await getStudentProfile();
              console.log('Student profile data:', profileData);
              
              setUser({
                ...profileData.user,
                is_staff: false
              });
            } catch (error) {
              console.error("Error fetching student profile:", error);
              handleLogout();
            }
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        handleLogout();
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, []);

  const handleLogin = (userData, tokens) => {
    console.log('Logging in with user data:', userData);
    console.log('Tokens:', tokens);
    
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    
    // Decode token to verify admin status
    const decodedToken = jwtDecode(tokens.access);
    const isAdminUser = decodedToken.is_staff || false;
    
    console.log('Login - Is admin:', isAdminUser);
    
    setIsAdmin(isAdminUser);
    
    // Modify user data to include admin status
    setUser({
      ...userData,
      is_staff: isAdminUser
    });
  };

  const handleLogout = () => {
    console.log('Logging out');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAdmin(false);
  };

  const updateUserProfile = (updatedProfile) => {
    console.log('Updating user profile:', updatedProfile);
    setUser(prev => {
      const newUser = {
        ...prev,
        ...updatedProfile
      };
      console.log('New user state:', newUser);
      return newUser;
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin,
    loading,
    login: handleLogin,
    logout: handleLogout,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;